<template>
  <VModal :showing="showing" container-classes="max-w-200" data-testid="replace-pages" @update:showing="cancel" @esc="cancel">
    <template #header>
      <h2 class="text-lg m-0">Bulk replace PDF source</h2>
      <div class="text-sm text-gray-600 font-normal">Select the pages that will replace your current slides</div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton variant="light" @click="$emit('back')">Back</VButton>
        <VButton :disabled="loading" variant="copper" data-testid="submit-replace-slides" @click="replaceSlides">Bulk replace</VButton>
      </div>
    </template>
    <template #default>
      <div class="replace-slides-container">
        <div class="block">
          <div class="text-gray-800 mb-9">
            VRIFY Slides
            <div v-for="slide in mutableSlidesToReplace" :key="slide.id" class="border border-gray-200 p-4 w-72 h-36">
              <div class="w-36 h-20 rounded-lg border border-gray-200 flex-shrink-0 overflow-hidden">
                <img v-if="slide.image && slide.image.id" class="h-full w-full" :src="slide.image.url" />
                <img v-else-if="slide.preview" class="h-full w-full" :src="slide.preview.url" />
                <PlaceholderDeck v-else />
              </div>
              <div class="flex items-center overflow-hidden">
                <div class="text-gray-800 text-sm mt-1 truncate">{{ slide.title }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-center items-center p-4">
          <ArrowLongDownIcon class="text-gray-300 w-16 h-12" />
        </div>
        <div class="block min-w-0">
          <p class="text-gray-800 mb-0 flex-shrink-0 truncate">New PDF Source: {{ pdfFile?.name }}</p>
          <div class="text-gray-800 text-sm font-light">
            Choose from a range of 1 - {{ pdfFile?.page_count ?? 0 }}
            <div v-for="(slide, index) in mutableSlidesToReplace" :key="slide.id" class="border border-gray-200 p-4 pb-6 w-72 h-36">
              <div class="flex items-center">
                <div class="w-36 h-20" :class="{ 'rounded-lg border border-gray-200': loading || images[getPageNumber(slide)] }">
                  <div v-if="loading" class="flex justify-center items-center w-full h-full rounded-lg">
                    <FontAwesomeIcon v-if="loading" class="text-gray-100" icon="circle-notch" spin size="3x" />
                  </div>
                  <img v-else-if="images[getPageNumber(slide)]" class="h-full w-full rounded-lg" :src="images[getPageNumber(slide)].url" />
                  <PlaceholderDeck v-else class="rounded-lg" />
                </div>
                <VInput
                  :key="slide.id"
                  :model-value="slide.page_number?.toString()"
                  class="ml-4 text-center w-12 h-10"
                  data-testid="replace-page-input"
                  :class="{ 'v-input__error': inputHasError(slide.id) }"
                  :error="inputHasError(slide.id)"
                  :show-error-icon="false"
                  placeholder="0"
                  size="sm"
                  @update:model-value="handleInput($event, slide)"
                />
              </div>
              <div v-if="inputHasError(slide.id)" class="error mt-3 text-red-800" :data-testid="`replace-page-error-${index}`">
                Choose from a range of 1 and {{ pdfFile?.page_count ?? 0 }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { onMounted, ref, toRef, watch } from 'vue'

import { ArrowLongDownIcon } from '@heroicons/vue/24/solid'
import type { ShelfFile } from '@/entities/shelfFile'
import type { SlideImage } from '@/entities/deck'
import type { Slide } from '@/entities/deck'

const props = defineProps({
  showing: {
    type: Boolean,
    required: true,
  },
  pdfFile: {
    type: Object as PropType<ShelfFile | null>,
    default: null,
  },
  slidesToReplace: {
    type: Array as PropType<Slide[] | null>,
    default: () => [],
  },
})

const emit = defineEmits(['close', 'back', 'bulk-replace-pdf-source'])

const loading = ref(false)
const errors = ref<number[]>([])
const images = ref<SlideImage[]>([])
const mutableSlidesToReplace = ref<Slide[]>([])

onMounted(() => {
  props.slidesToReplace?.forEach(slide => {
    if (slide) {
      const isValidPageNumber = slide.page_number && props.pdfFile?.page_count && slide.page_number <= props.pdfFile?.page_count

      mutableSlidesToReplace.value.push(
        JSON.parse(
          JSON.stringify({
            ...slide,
            page_number: isValidPageNumber ? slide.page_number : -1,
          })
        )
      )
    }
  })
})

watch(
  toRef(props, 'pdfFile'),
  async pdf => {
    if (pdf) {
      try {
        loading.value = true

        const data = await $fetch<SlideImage[]>(`v2/companies/${pdf.company_id}/shelf/files/${pdf.id}/previews`, {
          ...useFetchSharedOptions(),
        })

        images.value = data
      } catch (e) {
        console.error("Couldn't fetch images")
      } finally {
        loading.value = false
      }
    }
  },
  { immediate: true }
)

function cancel() {
  emit('close')
}

async function replaceSlides() {
  if (errors.value.length > 0) return

  const updatedSlides = mutableSlidesToReplace.value
    .filter(slide => !!slide.page_number)
    .map(slide => {
      slide.file_id = props.pdfFile?.id

      return slide
    })

  emit('bulk-replace-pdf-source', updatedSlides)
}

function validateInput({ slide, newPageNumber }: { slide: Slide; newPageNumber: number }) {
  if (!props.pdfFile?.page_count) return
  const isInvalidPageNumber = !newPageNumber || newPageNumber > props.pdfFile.page_count || newPageNumber <= 0

  if (newPageNumber && (isInvalidPageNumber || isNaN(newPageNumber))) {
    errors.value.push(slide.id)
  } else {
    _clearInputError(slide.id)
  }
}

function handleInput(newPageNumber: number, slide: Slide) {
  validateInput({ slide, newPageNumber })
  slide.page_number = newPageNumber
}

function getPageNumber(slide: Slide) {
  return (slide?.page_number ?? 0) - 1
}

function inputHasError(slideId: number) {
  return errors.value.includes(slideId)
}

const _clearInputError = (slideId: number) => {
  return (errors.value = errors.value.filter(s => slideId !== s))
}
</script>

<style lang="stylus">
.replace-slides-container
  @apply flex overflow-y-auto overflow-x-hidden justify-start p-4
  max-height: 60vh
</style>
